import Vue from 'vue';
import VueRouter from 'vue-router';
import BlogLayout from '@/layouts/BlogLayout.vue';
import AdminLayout from '@/layouts/AdminLayout.vue';
import BlogHome from '@/views/blog/Home.vue';
import ArticleList from '@/views/blog/ArticleList.vue';
import AdminDashboard from '@/views/admin/Dashboard.vue';
import AdminLogin from '@/views/admin/Login.vue';
import AdminCategories from '@/views/admin/Categories.vue';
import AdminPosts from '@/views/admin/Posts.vue';
import { isAdminLoggedIn } from '@/services/auth';
import { trackFrontendEvent } from '@/services/operationLogs';

Vue.use(VueRouter);

const ArticleDetail = () => import(/* webpackChunkName: "article-detail" */ '@/views/blog/ArticleDetail.vue');
const GlassTest = () => import(/* webpackChunkName: "glass-test" */ '@/views/blog/GlassTest.vue');
const AdminPublish = () => import(/* webpackChunkName: "admin-publish" */ '@/views/admin/Publish.vue');
const AdminProfile = () => import(/* webpackChunkName: "admin-profile" */ '@/views/admin/Profile.vue');
const AdminOperationLogs = () => import(/* webpackChunkName: "admin-operation-logs" */ '@/views/admin/OperationLogs.vue');
const AdminMenus = () => import(/* webpackChunkName: "admin-menus" */ '@/views/admin/Menus.vue');
const AdminSiteSettings = () => import(/* webpackChunkName: "admin-site-settings" */ '@/views/admin/SiteSettings.vue');
const SkillCenter = () => import(/* webpackChunkName: "skill-center" */ '@/views/blog/SkillCenter.vue');
const AdminSkills = () => import(/* webpackChunkName: "admin-skills" */ '@/views/admin/Skills.vue');
const AdminSkillPublish = () => import(/* webpackChunkName: "admin-skill-publish" */ '@/views/admin/SkillPublish.vue');

const routes = [
  {
    path: '/',
    component: BlogLayout,
    children: [
      {
        path: '',
        name: 'BlogHome',
        component: BlogHome,
      },
      {
        path: 'articles',
        name: 'ArticleList',
        component: ArticleList,
      },
      {
        path: 'articles/:id',
        name: 'ArticleDetail',
        component: ArticleDetail,
      },
      {
        path: 'skills',
        name: 'SkillCenter',
        component: SkillCenter,
      },
      {
        path: 'pic',
        name: 'GlassTest',
        component: GlassTest,
        meta: {
          blogBackgroundImage: '/api/public/background-images/random',
        },
      },
    ],
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: AdminLogin,
    meta: {
      guestOnly: true,
    },
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: AdminDashboard,
        meta: {
          adminTitle: '仪表盘',
        },
      },
      {
        path: 'articles',
        name: 'AdminPosts',
        component: AdminPosts,
        meta: {
          adminTitle: '文章管理页面',
        },
      },
      {
        path: 'articles/new',
        name: 'AdminPublish',
        component: AdminPublish,
        meta: {
          adminTitle: '发布文章页面',
        },
      },
      {
        path: 'articles/:id/edit',
        name: 'AdminArticleEdit',
        component: AdminPublish,
        meta: {
          adminTitle: '编辑文章页面',
        },
      },
      {
        path: 'menus',
        name: 'AdminMenus',
        component: AdminMenus,
        meta: {
          adminTitle: '菜单管理页面',
        },
      },
      {
        path: 'site-settings',
        name: 'AdminSiteSettings',
        component: AdminSiteSettings,
        meta: {
          adminTitle: '站点设置页面',
        },
      },
      {
        path: 'categories',
        name: 'AdminCategories',
        component: AdminCategories,
        meta: {
          adminTitle: '分类管理页面',
        },
      },
      {
        path: 'skills',
        name: 'AdminSkills',
        component: AdminSkills,
        meta: {
          adminTitle: 'SKILL管理页面',
        },
      },
      {
        path: 'skills/upload',
        name: 'AdminSkillUpload',
        redirect: '/admin/skills/publish',
      },
      {
        path: 'skills/publish',
        name: 'AdminSkillPublish',
        component: AdminSkillPublish,
        meta: {
          adminTitle: 'SKILL发布页面',
        },
      },
      {
        path: 'password',
        name: 'AdminPassword',
        redirect: '/admin/profile',
      },
      {
        path: 'profile',
        name: 'AdminProfile',
        component: AdminProfile,
        meta: {
          adminTitle: '个人信息页面',
        },
      },
      {
        path: 'operation-logs',
        name: 'AdminOperationLogs',
        component: AdminOperationLogs,
        meta: {
          adminTitle: '用户日志页面',
        },
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        selector: to.hash,
        offset: {
          x: 0,
          y: 88,
        },
      };
    }

    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  const loggedIn = isAdminLoggedIn();

  if (to.matched.some((record) => record.meta.requiresAuth) && !loggedIn) {
    next({
      path: '/admin/login',
      query: {
        redirect: to.fullPath,
      },
    });
    return;
  }

  if (to.meta.guestOnly && loggedIn) {
    next('/admin');
    return;
  }

  next();
});

router.afterEach((to, from) => {
  if (!to.matched.some((record) => record.meta.requiresAuth) || !isAdminLoggedIn()) {
    return;
  }

  const adminTitle = [...to.matched]
    .reverse()
    .find((record) => record.meta?.adminTitle)
    ?.meta.adminTitle || '后台页面';

  trackFrontendEvent({
    operation: `访问${adminTitle}`,
    pagePath: to.path,
    detail: from?.fullPath ? `从 ${from.fullPath} 进入 ${to.fullPath}` : `进入 ${to.fullPath}`,
    success: true,
  });
});

export default router;
